import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Loader2, Mail, Phone, MapPin, CalendarDays,
  ExternalLink, MessageSquare, LayoutGrid, Building2,
} from "lucide-react";
import { toast } from "sonner";
import { adminDemoLeads } from "@/lib/api";

const ORIGIN = window.location.origin;

const fmtDate = (s) => { try { return new Date(s).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); } catch { return s; } };

function TypeBadge({ type }) {
  const booking = type === "booking";
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${booking ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-brand"}`}>
      {booking ? "Booking" : "Chat lead"}
    </span>
  );
}

export default function AdminDemoLeads() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(null); // demo id or null (all)
  const [active, setActive] = useState(null);

  const load = () => {
    adminDemoLeads()
      .then((d) => setData(d))
      .catch(() => { toast.error("Failed to load demo leads"); setData({ leads: [], industries: [], total_all: 0 }); });
  };
  useEffect(() => { load(); }, []);

  const leads = useMemo(() => {
    if (!data) return [];
    return filter ? data.leads.filter((l) => l.demo === filter) : data.leads;
  }, [data, filter]);

  const openDemo = (l) => { if (l.demo_path) window.open(`${ORIGIN}${l.demo_path}`, "_blank", "noopener"); };

  return (
    <main className="min-h-screen bg-slate-50" data-testid="admin-demo-leads">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-3"><ArrowLeft size={14} /> Back to console</Link>
        <div className="flex items-center gap-2">
          <Sparkles size={22} className="text-brand" />
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900">Demo Leads</h1>
        </div>
        <p className="text-slate-500 mt-1">Every enquiry &amp; booking captured by the AI Assistants across your demo sites — filter by industry and open the exact page a lead came from.</p>

        {data === null ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin text-brand" size={26} /></div>
        ) : (
          <>
            {/* Industry filter chips */}
            <div className="flex flex-wrap gap-2 mt-8" data-testid="demo-leads-filters">
              <button onClick={() => { setFilter(null); setActive(null); }} data-testid="demo-filter-all"
                className={`inline-flex items-center gap-1.5 text-sm rounded-full px-4 py-2 border transition-colors ${!filter ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                <LayoutGrid size={14} /> All industries <span className="opacity-70">({data.total_all})</span>
              </button>
              {data.industries.map((ind) => (
                <button key={ind.demo} onClick={() => { setFilter(ind.demo); setActive(null); }} data-testid={`demo-filter-${ind.demo}`}
                  className={`inline-flex items-center gap-1.5 text-sm rounded-full px-4 py-2 border transition-colors ${filter === ind.demo ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                  {ind.business || ind.label} <span className="opacity-70">({ind.count})</span>
                </button>
              ))}
            </div>

            {leads.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-10 mt-6 text-center text-slate-400" data-testid="demo-leads-empty">
                No demo leads captured yet. They appear here when a visitor shares their contact with a demo AI Assistant.
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                {/* List */}
                <div className="lg:col-span-2 space-y-3">
                  {leads.map((l) => (
                    <button key={l.lead_key} onClick={() => setActive(l)} data-testid={`demo-lead-row-${l.lead_key}`}
                      className={`w-full text-left bg-white border rounded-xl card-shadow p-4 transition-colors ${active?.lead_key === l.lead_key ? "border-brand ring-1 ring-brand/20" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{l.name || l.email || "Visitor"}</div>
                          <div className="text-xs text-slate-400 flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className="inline-flex items-center gap-1"><Building2 size={11} /> {l.business}</span>
                            {l.email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {l.email}</span>}
                          </div>
                        </div>
                        <div className="text-right shrink-0 space-y-1">
                          <TypeBadge type={l.type} />
                          <div className="text-[11px] text-slate-400">{fmtDate(l.created_at)}</div>
                        </div>
                      </div>
                      {l.appointment && (
                        <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">
                          <CalendarDays size={12} /> {l.appointment.service} · {l.appointment.date} {l.appointment.time}
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {/* Detail */}
                <div className="lg:col-span-1">
                  {active ? (
                    <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 sticky top-6" data-testid="demo-lead-detail">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{active.vertical}</span>
                        <TypeBadge type={active.type} />
                      </div>
                      <div className="font-heading text-lg font-bold text-slate-900 mt-2">{active.name || active.email || "Visitor"}</div>
                      <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><Building2 size={13} /> {active.business}{active.assistant ? ` · ${active.assistant}` : ""}</div>

                      <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                        {active.email && <div className="flex items-center gap-2"><Mail size={13} className="text-slate-400" /> <a href={`mailto:${active.email}`} className="hover:text-brand">{active.email}</a></div>}
                        {active.phone && <div className="flex items-center gap-2"><Phone size={13} className="text-slate-400" /> {active.phone}</div>}
                        {active.geo?.city && <div className="flex items-center gap-2"><MapPin size={13} className="text-slate-400" /> {[active.geo.city, active.geo.country].filter(Boolean).join(", ")}</div>}
                        <div className="flex items-center gap-2 text-xs text-slate-400 pt-0.5"><CalendarDays size={12} /> {fmtDate(active.created_at)}</div>
                      </div>

                      {active.appointment && (
                        <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                          <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">Booking</div>
                          <div className="text-sm text-emerald-900">{active.appointment.service}</div>
                          <div className="text-sm text-emerald-800">{active.appointment.date} at {active.appointment.time}</div>
                          <div className="text-[11px] text-emerald-700 mt-0.5 capitalize">Status: {active.appointment.status}</div>
                        </div>
                      )}

                      {/* Demo URL — the page this lead came from */}
                      {active.demo_path && (
                        <button onClick={() => openDemo(active)} data-testid="demo-lead-open-site"
                          className="mt-4 w-full text-left rounded-lg border border-slate-200 hover:border-brand transition-colors p-3 flex items-center justify-between gap-2 group">
                          <div className="min-w-0">
                            <div className="text-[11px] uppercase tracking-wider text-slate-400">Captured on</div>
                            <div className="text-sm text-brand truncate">{ORIGIN}{active.demo_path}</div>
                          </div>
                          <ExternalLink size={15} className="text-slate-400 group-hover:text-brand shrink-0" />
                        </button>
                      )}

                      {/* Query / transcript */}
                      <div className="mt-4">
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><MessageSquare size={12} /> The query</div>
                        {active.transcript?.length > 0 ? (
                          <div className="space-y-2 max-h-72 overflow-y-auto rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid="demo-lead-transcript">
                            {active.transcript.map((t, i) => (
                              <div key={i} className={`text-sm ${t.role === "user" ? "text-slate-800" : "text-slate-500"}`}>
                                <span className="font-semibold">{t.role === "user" ? "Visitor" : (active.assistant || "Assistant")}: </span>
                                <span className="whitespace-pre-wrap">{t.content}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500 whitespace-pre-wrap bg-slate-50 border border-slate-100 rounded-lg p-3">{active.message || "—"}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 text-center text-slate-400 text-sm">Select a lead to see its details, query and demo URL.</div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
